import { useEffect } from "react";
import { budgetSnapshot, durationBetween, getNumericAssumption, todayIso } from "../lib/calculations";
import { activityTypes, type AppData, type TeamMember, type WorkActivity } from "../types";

function record(input: unknown): Record<string, unknown> {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new Error("Input must be an object.");
  }
  return input as Record<string, unknown>;
}

export function useWebMcp({ data, profile, saveActivity }: { data: AppData; profile: TeamMember; saveActivity(activity: WorkActivity): Promise<void> }) {
  useEffect(() => {
    const context = document.modelContext;
    if (!context?.registerTool) return;
    const lifecycle = new AbortController();

    const register = (tool: ModelContextTool) => {
      try {
        void Promise.resolve(context.registerTool(tool, { signal: lifecycle.signal })).catch(() => undefined);
      } catch {
        // WebMCP is optional and unsupported browsers continue normally.
      }
    };

    register({
      name: "list_recent_work_logs",
      title: "List recent work logs",
      description: "Read recent House Care work entries, optionally filtered to an ISO month such as 2026-09.",
      inputSchema: {
        type: "object",
        properties: { month: { type: "string", pattern: "^\\d{4}-\\d{2}$" }, limit: { type: "integer", minimum: 1, maximum: 50 } },
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute(input) {
        const values = record(input);
        const month = typeof values.month === "string" ? values.month : "";
        const limit = typeof values.limit === "number" ? Math.min(50, Math.max(1, values.limit)) : 10;
        return data.activities.filter((item) => !month || item.work_date.startsWith(month)).slice(0, limit).map((item) => ({ id: item.id, workDate: item.work_date, client: data.clients.find((client) => client.id === item.client_id)?.client_name || null, activity: item.activity_type, status: item.status, durationMinutes: item.duration_minutes, amountReceived: item.amount_received, petrolLitres: item.petrol_litres }));
      },
    });

    register({
      name: "get_monthly_budget_snapshot",
      title: "Get monthly budget snapshot",
      description: "Read the current workbook-based income, operating cost, reserve and spendable cash totals.",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: false },
      execute() {
        return budgetSnapshot(data.clients, data.monthlyCosts, data.tools, data.assumptions);
      },
    });

    register({
      name: "create_work_log",
      title: "Create work log",
      description: "Create and save one daily House Care work entry for the signed-in team member.",
      inputSchema: {
        type: "object",
        properties: {
          clientName: { type: "string" },
          workDate: { type: "string", pattern: "^\\d{4}-\\d{2}-\\d{2}$" },
          activityType: { type: "string", enum: [...activityTypes] },
          description: { type: "string" },
          startTime: { type: "string", pattern: "^\\d{2}:\\d{2}$" },
          endTime: { type: "string", pattern: "^\\d{2}:\\d{2}$" },
          amountReceived: { type: "number", minimum: 0 },
          petrolLitres: { type: "number", minimum: 0 },
          notes: { type: "string" },
        },
        required: ["activityType"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, untrustedContentHint: true },
      async execute(input) {
        const values = record(input);
        if (typeof values.activityType !== "string" || !values.activityType.trim()) throw new Error("activityType is required.");
        if (!activityTypes.includes(values.activityType as (typeof activityTypes)[number])) throw new Error("activityType is not supported.");
        const requestedClient = typeof values.clientName === "string" ? values.clientName.toLowerCase() : "";
        const client = requestedClient ? data.clients.find((item) => item.client_name.toLowerCase() === requestedClient) : undefined;
        if (values.clientName && !client) throw new Error("Client name was not found.");
        const petrolLitres = typeof values.petrolLitres === "number" ? values.petrolLitres : 0;
        const startTime = typeof values.startTime === "string" ? values.startTime : null;
        const endTime = typeof values.endTime === "string" ? values.endTime : null;
        const amountReceived = typeof values.amountReceived === "number" ? values.amountReceived : 0;
        const entry: WorkActivity = {
          id: crypto.randomUUID(),
          work_date: typeof values.workDate === "string" ? values.workDate : todayIso(),
          team_member_id: profile.user_id,
          client_id: client?.id || null,
          activity_type: values.activityType as WorkActivity["activity_type"],
          description: typeof values.description === "string" ? values.description : "",
          start_time: startTime,
          end_time: endTime,
          duration_minutes: startTime && endTime ? durationBetween(startTime, endTime) : 0,
          status: "Completed",
          amount_charged: client?.amount_per_payment || 0,
          amount_received: amountReceived,
          payment_status: amountReceived > 0 ? "Paid" : "Unpaid",
          petrol_litres: petrolLitres,
          two_stroke_oil_ml: petrolLitres * getNumericAssumption(data.assumptions, "two_stroke_oil_mix_ml_per_litre", 20),
          trimmer_line_cost: 0,
          refuse_bags_cost: 0,
          maintenance_cost: 0,
          materials_cost: 0,
          transport_cost: 0,
          phone_data_cost: 0,
          other_cost: 0,
          tools_used: petrolLitres > 0 ? ["Petrol brush cutter / weed cutter"] : [],
          notes: typeof values.notes === "string" ? values.notes : "",
        };
        await saveActivity(entry);
        return { id: entry.id, status: "saved", workDate: entry.work_date, client: client?.client_name || null };
      },
    });

    return () => lifecycle.abort();
  }, [data, profile, saveActivity]);
}
